<script setup>
// Reusable preview header
// Props:
// - title (string)
// - to (route location object or string)
// - buttonLabel (string, default 'Öffnen')
// - dense (boolean) for slightly tighter spacing
// - disabled (boolean) to disable the action button
import { computed } from 'vue'

const props = defineProps({
  title: { type: String, required: true },
  to: { type: [String, Object], required: false },
  debugInfo: { type: Object, required: false, default: null },
  summary: { type: String, default: '' },
  buttonLabel: { type: String, default: 'Öffnen' },
  dense: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
  loadingDisabled: { type: Boolean, default: false },
  arrow: { type: String, default: 'right' },
  hitCount: { type: Number, default: 0 }
});
const headerClass = computed(()=> ({ dense: props.dense }))

// Button ist deaktiviert, wenn disabled-Prop true ist (volle Kontrolle von außen)
const isButtonDisabled = computed(() => props.disabled);

// Highlight numbers in summary (e.g. '247 Treffer')
function formatSummary(summary) {
  // Replace first number (including thousands separator) with a styled span
  return summary.replace(/(\d{1,3}(?:\.\d{3})*)/, '<span class="ph-summary-number">$1</span>');
}
</script>

<template>
  <div class="preview-header" :class="headerClass">
    <!-- DEBUG: Button-Bedingungen -->
    <div v-if="props.debugInfo" style="background:#fffbe6; color:#b45309; border:1px solid #ffe29a; border-radius:6px; padding:0.5em 1em; margin-bottom:0.5em; font-size:0.79em; line-height:1.6;">
      <strong>⚠ PreviewHeader – Button-Bedingungen:</strong><br>
      <span :style="{ fontWeight: props.disabled ? 'bold' : 'normal', color: props.disabled ? '#b45309' : '#555' }">disabled: {{ props.disabled }}</span><br>
      <span :style="{ fontWeight: props.loadingDisabled ? 'bold' : 'normal', color: props.loadingDisabled ? '#b45309' : '#555' }">loadingDisabled: {{ props.loadingDisabled }}</span><br>
      <span :style="{ fontWeight: !props.to ? 'bold' : 'normal', color: !props.to ? '#b45309' : '#555' }">to (aktuell gerendert): {{ JSON.stringify(props.to) }} → {{ props.to ? '✅ router-link' : '⚠ kein to → button' }}</span><br>
      <span v-if="props.debugInfo && Object.prototype.hasOwnProperty.call(props.debugInfo, 'expectedTo')">
        to (erwartet): {{ JSON.stringify(props.debugInfo.expectedTo) }}
      </span>
      <br v-if="props.debugInfo && Object.prototype.hasOwnProperty.call(props.debugInfo, 'expectedTo')" />
      buttonLabel: {{ props.buttonLabel }}<br>
      hitCount: {{ props.hitCount }}<br>
      <em style="font-size:0.9em;">Button erscheint als router-link wenn: !disabled &amp;&amp; !loadingDisabled &amp;&amp; to</em>
    </div>
    <div class="ph-title-wrap">
      <h2 class="ph-title">{{ title }}</h2>
      <span v-if="summary" class="ph-summary" v-html="formatSummary(summary)"></span>
      <slot name="summary-extra" />
    </div>
    <!-- {{ hitCount }} -->
    <div class="ph-btn-wrap">
      <router-link v-if="!isButtonDisabled && !loadingDisabled && to" class="ph-btn" :class="{'arrow-left': arrow === 'left', 'arrow-right': arrow !== 'left'}" :to="to">
        <slot name="icon" />
        {{ buttonLabel }}
      </router-link>
      <button v-else-if="!isButtonDisabled && !loadingDisabled && !to" type="button" class="ph-btn" :class="{'arrow-left': arrow === 'left', 'arrow-right': arrow !== 'left'}">
        <slot name="icon" />
        {{ buttonLabel }}
      </button>
      <span v-else-if="loadingDisabled" class="ph-btn is-loading-disabled" :class="{'arrow-left': arrow === 'left', 'arrow-right': arrow !== 'left'}" aria-disabled="true">
        <slot name="icon" />
        {{ buttonLabel }}
      </span>
      <span
        v-else
        class="ph-btn"
        :class="{
          'is-disabled': hitCount === 0,
          'arrow-left': arrow === 'left',
          'arrow-right': arrow !== 'left'
        }"
        v-bind="hitCount === 0 ? { 'aria-disabled': 'true' } : {}"
      >
        <slot name="icon" />
        {{ buttonLabel }}
      </span>
      <slot name="below-btn" />
    </div>
  </div>
</template>

<style lang="scss" scoped>
/* Header layout with summary and button right-aligned */
.preview-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  width: 100%;
  &:not(:has(.collapse-filter-btn)) {
    flex-direction: row;
    justify-content: space-between;
  }
}

.preview-header.dense {
  gap: .75rem;
}
.ph-title-wrap { display: flex; flex-direction: column; align-items: flex-start; gap: 0.1em; min-width: 0; }
.ph-btn-wrap {
  display: flex;
  align-items: center;
  flex-direction: row;
  gap: .4rem;
  @media (min-width: 1024px) {
    flex-direction: column;
    align-items: stretch;
  }
  &:has(.collapse-filter-btn) {
    justify-content: space-between; 
    width: 100%;
  }
}
.ph-title { margin:0; font-size:.95rem; letter-spacing:.45px; font-weight:600; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.ph-summary {
  font-size: 1.04em;
  color: #334155;
  font-weight: 600;
  margin-top: 0.1em;
  /* white-space:nowrap; */
  overflow:hidden;
  text-overflow:ellipsis;
  background: linear-gradient(90deg, #f8fafc 60%, #f3f4f6 100%);
  border-radius: 8px;
  border: 1.5px solid #cbd5e1;
  padding: 0.18em 1.1em 0.18em 0.7em;
  display: inline-block;
  box-shadow: 0 2px 8px 0 rgba(100,116,139,0.07), 0 1px 2px 0 rgba(0,0,0,0.04);
  letter-spacing: 0.01em;
  width: 100%;
  overflow: auto;
}
.ph-summary-number {
  color: #0f172a;
  font-weight: 900;
  font-size: 1.13em;
  letter-spacing: 0.7px;
  margin-right: 0.32em;
  background: #fff;
  border: 2.5px solid #cbd5e1;
  border-left: 4px solid #94a3b8;
  border-right: 4px solid #94a3b8;
  border-radius: 9999px;
  padding: 0.13em 1.5em 0.13em 1.5em;
  box-shadow: 0 2px 8px 0 rgba(100,116,139,0.13);
  display: inline-block;
  vertical-align: middle;
  text-shadow: 0 1px 6px #e5e7eb, 0 1px 1px #fff;
  filter: brightness(1.04);
  transition: background 0.18s, border 0.18s;
}
/* Button style with stronger active contrast to avoid disabled look */
.ph-btn { font-size:.6rem; font-weight:700; text-decoration:none; background:#e7f1fa; border:1px solid #86a8c5; padding:.4rem .65rem; border-radius:8px; color:#13334c; display:inline-flex; gap:.35rem; align-items:center; letter-spacing:.05em; line-height:1; transition:.18s background, .18s color, .18s border-color, .18s box-shadow; position:relative; box-shadow:0 1px 0 rgba(19,51,76,.15); }
.ph-btn.arrow-left:before { content:'\2190'; /* left arrow */ font-size:.7rem; line-height:1; margin-right:.15rem; }
.ph-btn.arrow-right:after { content:'\2192'; /* right arrow */ font-size:.7rem; line-height:1; margin-left:.15rem; }
.ph-btn:hover { background:#d6e8f8; border-color:#5e8db3; color:#0f2a40; }
/* Dark theme variant (mirrors PerformanceRanks .back-btn) */
.theme-dark .ph-btn, .theme--dark .ph-btn { background:#24445b; border-color:#3a6b8a; color:#ecf7ff; box-shadow:0 1px 0 rgba(3,10,16,.45); }
.theme-dark .ph-btn:hover, .theme--dark .ph-btn:hover { background:#2d5571; border-color:#4a84ab; }
/* Light theme explicit override (keep contrast) */
.theme--light .ph-btn { background:#e7f1fa; border-color:#86a8c5; color:#13334c; }
/* Light theme hover (explicit to avoid being overridden by scoped order) */
.theme--light .ph-btn:hover { background:#d6e8f8; border-color:#5e8db3; color:#0f2a40; }
.ph-btn:focus-visible { outline:2px solid #94a3b8; outline-offset:2px; }
.ph-btn.is-disabled { background:#fee2e2; border-color:#fca5a5; color:#b91c1c; opacity:.45; cursor:not-allowed; }
.theme-dark .ph-btn.is-disabled, .theme--dark .ph-btn.is-disabled { background:#3b0d0d; border-color:#7f1d1d; color:#fca5a5; }
.ph-btn.is-loading-disabled { background:#e5e7eb; border-color:#cbd5e1; color:#64748b; opacity:.65; cursor:not-allowed; }
.theme-dark .ph-btn.is-loading-disabled, .theme--dark .ph-btn.is-loading-disabled { background:#23272f; border-color:#334155; color:#94a3b8; }
</style>
